import React, { useState } from 'react';
import { HiOutlineX, HiOutlineDocumentText } from "react-icons/hi";
import { TailSpin } from "react-loader-spinner";
import { useSelector } from "react-redux";
import { selectCid } from "../../features/companyid";
import { selectUuid } from '../../features/userid';
import Tooltip from '../ui/Tooltip';

const AssignCandidateModal = ({ candidate, onClose, onConfirm, allJobs }) => {
    const cid = useSelector(selectCid);
    const user = useSelector(selectUuid);
    const [selectedJob, setSelectedJob] = useState(null);
    const [notes, setNotes] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSkillsExpanded, setIsSkillsExpanded] = useState(false);
    const suggestedJobs = allJobs?.jobs?.map((job) => ({
        uuid: job.job_id,
        label: job.title
    })) || [];

    const handleConfirm = async () => {
        if (!selectedJob) return;
        setIsSubmitting(true);
        try {
            await onConfirm({
                candidate_uuid: candidate.candidate_uuid,
                job_id: selectedJob,
                company_id: cid,
                recruiter_uuid: user,
                reason: notes
            });
            onClose();
            window.location.reload();
        } catch (error) {
            console.error("Assignment failed", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div>
                    <div className="p-6 pb-4 flex justify-between items-start border-b border-gray-100">

                        <div className="flex flex-col gap-1">
                            <h2 className="text-2xl font-bold text-gray-900">{candidate.name || "Anonymous Candidate"}</h2>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => window.open(candidate.resume_url, '_blank')}
                                className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                            >
                                <HiOutlineDocumentText className="text-lg" />
                                Resume
                            </button>
                            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                                <HiOutlineX className="text-xl text-gray-400" />
                            </button>
                        </div>
                    </div>

                    <div className="mt-4 px-6">
                        <h3 className="text-sm font-bold text-gray-900 mb-2">Skills</h3>
                        <div className="flex flex-wrap gap-2 w-full">
                            {!isSkillsExpanded ? (
                                <>
                                    {(candidate.skills || []).slice(0, 5).map((skill, i) => {
                                        const truncatedSkill = skill.length > 100 ? skill.substring(0, 97) + "..." : skill;
                                        return (
                                            <span key={i} className="px-3 py-1 bg-slate-200 text-slate-600 rounded-md text-sm font-medium border border-slate-100">
                                                {truncatedSkill}
                                            </span>
                                        );
                                    })}
                                    {(candidate.skills || []).length > 5 && (
                                        <button
                                            onClick={() => setIsSkillsExpanded(true)}
                                            className="px-3 py-1 bg-web-blue/10 text-web-blue rounded-md text-sm font-medium border border-web-blue/20 hover:bg-web-blue/20 transition-colors"
                                        >
                                            +{(candidate.skills || []).length - 5} Show More
                                        </button>
                                    )}
                                </>
                            ) : (
                                <div className="w-full">
                                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto pr-2 custom-scrollbar">
                                        {(candidate.skills || []).map((skill, i) => (
                                            <span key={i} className="px-3 py-1 bg-slate-200 text-slate-600 rounded-md text-sm font-medium border border-slate-100">
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                    <button
                                        onClick={() => setIsSkillsExpanded(false)}
                                        className="mt-2 text-xs font-bold text-web-blue hover:underline"
                                    >
                                        Show Less
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8">
                    {/* Executive Summary */}
                    <div className="bg-blue-50/30 border border-blue-100/50 rounded-xl p-5">
                        <h3 className="text-sm font-bold text-gray-900 mb-3">Executive summary</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                            {candidate.summary || "No summary available for this candidate."}
                        </p>
                    </div>

                    {/* Manual Assignment */}
                    <div className="pt-6 border-t border-dashed border-gray-200 space-y-4">
                        <h3 className="font-bold text-gray-900">Or, Assign Manually</h3>

                        <div className="space-y-4">
                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700">Select a Job*</label>
                                <select
                                    className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all text-sm appearance-none bg-no-repeat bg-[right_1rem_center] bg-[length:1em]"
                                    value={selectedJob || ""}
                                    onChange={(e) => setSelectedJob(e.target.value)}
                                >
                                    <option value="" disabled>Select Job</option>
                                    {suggestedJobs?.map(job => (
                                        <option key={job.uuid} value={job.uuid}>{job.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-sm font-bold text-gray-700">Notes</label>
                                <textarea
                                    rows="3"
                                    placeholder="Anything here"
                                    className="w-full p-4 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all text-sm resize-none"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-100 flex items-center justify-center gap-4 bg-gray-50/50">
                    <button
                        onClick={onClose}
                        className="px-12 py-3 border border-gray-200 rounded-xl text-gray-600 font-bold hover:bg-white hover:border-gray-300 transition-all"
                    >
                        Close
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!selectedJob || isSubmitting}
                        className={`px-12 py-3 rounded-xl font-bold transition-all shadow-md ${(!selectedJob || isSubmitting) ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none' : 'bg-[#111827] text-white hover:bg-black shadow-black/20'}`}
                    >
                        {isSubmitting ? (
                            <div className="flex items-center gap-2">
                                <TailSpin color="white" height={16} width={16} />
                                Assigning...
                            </div>
                        ) : 'Confirm'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssignCandidateModal;
